import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import AssignedTrucksModal from './AssignedTrucksModal';
import TruckAssignmentModal from './TruckAssignmentModal';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

const ActiveBookingsTab = () => {
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [truckCounts, setTruckCounts] = useState({});
  const [assignedTrucksModal, setAssignedTrucksModal] = useState(null);
  const [editAssignmentModal, setEditAssignmentModal] = useState(null);
  const [dispatchingId, setDispatchingId] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [adminVsrModal, setAdminVsrModal] = useState(null);
  const [adminVsrForm, setAdminVsrForm] = useState({ newVehicle: '', reason: 'breakdown' });
  const [adminVsrSubmitting, setAdminVsrSubmitting] = useState(false);
  const [pendingVsrBookingIds, setPendingVsrBookingIds] = useState(new Set());
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [preset, setPreset] = useState('till_date');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 5000);
  };

  const handleAdminVsrSubmit = async () => {
    if (!adminVsrForm?.newVehicle?.trim()) {
      showNotification('Please enter the new vehicle number.', 'error');
      return;
    }
    setAdminVsrSubmitting(true);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const userId = session?.user?.id;

      const { error } = await supabase
        ?.from('vehicle_substitution_requests')
        ?.insert({
          booking_id: adminVsrModal?.id,
          assignment_id: null,
          readable_booking_id: adminVsrModal?.readableBookingId || adminVsrModal?.poNumber,
          old_vehicle_number: (adminVsrModal?.assignedVehicle || '').toUpperCase(),
          new_vehicle_number: adminVsrForm?.newVehicle?.trim()?.toUpperCase(),
          reason: adminVsrForm?.reason,
          status: 'pending',
          requested_by_user_id: userId,
          requested_via: 'admin',
        });

      if (error) throw error;

      setAdminVsrModal(null);
      setAdminVsrForm({ newVehicle: '', reason: 'breakdown' });
      showNotification('Vehicle substitution request raised. Pending VAHAN approval.');
    } catch (err) {
      showNotification(`Failed: ${err?.message}`, 'error');
    }
    setAdminVsrSubmitting(false);
  };

  const loadActiveOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('buyer_bookings')
        ?.select('*, readable_booking_id, readable_offer_id')
        ?.in('status', ['approved', 'active', 'confirmed', 'do_generated', 'do_generated_partial', 'in_transit'])
        ?.order('booking_date', { ascending: false });

      if (!error && data) {
        const mapped = data?.map(row => ({
          id: row?.id,
          offerId: row?.readable_booking_id || row?.readable_offer_id || row?.offer_id || row?.id?.slice(0, 8)?.toUpperCase(),
          buyer: row?.buyer_email || '—',
          buyerId: row?.buyer_id || null,
          factoryName: row?.factory_name || '—',
          sugarGrade: row?.grade || '—',
          quantity: row?.quantity || 0,
          ratePerTon: row?.rate_per_ton || 0,
          totalAmount: row?.total_amount || 0,
          status: row?.status || 'approved',
          created_at: row?.created_at || null,
          bookingDate: row?.booking_date
            ? new Date(row?.booking_date)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : '—',
          bookingType: row?.booking_type || 'sugar_only',
          noOfTrucks: row?.no_of_trucks || 0,
          destination: row?.destination || '—',
          assignedVehicle: row?.assigned_vehicle_number || '',
          transporterRate: row?.transporter_rate_per_ton || 0,
          approvedAt: row?.approved_at
            ? new Date(row?.approved_at)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : '—',
          qtyRequired: row?.quantity || 0,
          poNumber: row?.readable_booking_id || row?.readable_offer_id || row?.offer_id || row?.id?.slice(0, 8)?.toUpperCase(),
          deliveryDestination: row?.destination || '',
          readableBookingId: row?.readable_booking_id || null,
          readableOfferId: row?.readable_offer_id || null,
        }));
        setActiveOrders(mapped);

        const allIds = mapped?.map(o => o?.id);
        if (allIds?.length > 0) {
          const { data: truckData } = await supabase
            ?.from('order_truck_assignments')
            ?.select('booking_id')
            ?.in('booking_id', allIds);

          if (truckData) {
            const counts = {};
            truckData?.forEach(t => {
              counts[t?.booking_id] = (counts?.[t?.booking_id] || 0) + 1;
            });
            setTruckCounts(counts);
          }

          const { data: vsrData } = await supabase
            ?.from('vehicle_substitution_requests')
            ?.select('booking_id')
            ?.in('booking_id', allIds)
            ?.eq('status', 'pending');
          const vsrSet = new Set((vsrData || [])?.map(v => v?.booking_id));
          setPendingVsrBookingIds(vsrSet);
        }
      }
    } catch (err) {
      console.error('Error loading active orders:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadActiveOrders();

    const bookingsChannel = supabase
      ?.channel('buyer_bookings_active_tab')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'buyer_bookings' }, () => {
        loadActiveOrders();
      })
      ?.subscribe();

    const trucksChannel = supabase
      ?.channel('order_truck_assignments_active_tab')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'order_truck_assignments' }, () => {
        loadActiveOrders();
      })
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'vehicle_substitution_requests' }, () => {
        loadActiveOrders();
      })
      ?.subscribe();
    return () => {
      supabase?.removeChannel(bookingsChannel);
      supabase?.removeChannel(trucksChannel);
    };
  }, [loadActiveOrders]);

  const { from: dateFrom, to: dateTo } = getDateRange(preset, customFrom, customTo);

  const filteredOrders = activeOrders.filter(o => {
    const text = searchText.toLowerCase();
    if (text && ![
      o.offerId, o.poNumber, o.factoryName, o.sugarGrade, o.buyer, o.destination
    ].some(f => (f || '').toLowerCase().includes(text))) return false;
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (dateFrom && new Date(o.created_at) < dateFrom) return false;
    if (dateTo && new Date(o.created_at) > dateTo) return false;
    return true;
  });

  const isBundleType = (bookingType) => {
    if (!bookingType) return false;
    const lower = bookingType?.toLowerCase();
    return lower?.includes('sugar') && lower?.includes('truck')
      || lower?.includes('bundle')
      || lower === 'sugar_truck';
  };

  const formatAmount = (amount) =>
    '₹' + Number(amount)?.toLocaleString('en-IN');

  const getStatusBadge = (status) => {
    const styles = {
      approved: { bg: '#dcfce7', color: '#166534', label: 'Approved' },
      active: { bg: '#dbeafe', color: '#1e40af', label: 'Active' },
      confirmed: { bg: '#d1fae5', color: '#065f46', label: 'Confirmed' },
      do_generated: { bg: '#ede9fe', color: '#5b21b6', label: 'DO Generated' },
      do_generated_partial: { bg: '#fef3c7', color: '#d97706', label: 'DO Partial' },
      in_transit: { bg: '#e0f2fe', color: '#0369a1', label: 'In Transit' },
    };
    const s = styles?.[status] || styles?.approved;
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
        style={{ background: s?.bg, color: s?.color }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s?.color }} />
        {s?.label}
      </span>
    );
  };

  const renderOfferIdCell = (order) => {
    const isBundle = isBundleType(order?.bookingType);
    const count = truckCounts?.[order?.id] || 0;

    if (!isBundle) {
      return (
        <span className="font-semibold" style={{ color: '#166534' }}>
          {order?.offerId}
        </span>
      );
    }

    return (
      <button
        onClick={() => setAssignedTrucksModal(order)}
        className="flex flex-col items-start gap-1 group text-left"
        title="Click to view assigned trucks"
      >
        <span
          className="font-semibold underline decoration-dotted group-hover:decoration-solid transition-all"
          style={{ color: '#0369a1' }}
        >
          {order?.poNumber}
        </span>
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold"
          style={{ background: count > 0 ? '#dbeafe' : '#f3f4f6', color: count > 0 ? '#1e40af' : '#9ca3af' }}
        >
          <Icon name="Truck" size={10} color={count > 0 ? '#1e40af' : '#9ca3af'} />
          {count > 0 ? `${count} truck${count !== 1 ? 's' : ''} allotted` : 'No trucks yet'}
        </span>
      </button>
    );
  };

  const handleInvoiceGenerated = async (order) => {
    if (dispatchingId) return;
    setDispatchingId(order?.id);

    try {
      const { data: pendingVsr, error: vsrErr } = await supabase
        ?.from('vehicle_substitution_requests')
        ?.select('id')
        ?.eq('booking_id', order?.id)
        ?.eq('status', 'pending')
        ?.limit(1);

      if (vsrErr) throw vsrErr;

      if (pendingVsr?.length > 0) {
        showNotification('Approve vehicle change first before dispatching.', 'error');
        setDispatchingId(null);
        return;
      }

      const isST = isBundleType(order?.bookingType);
      const { error: tpErr } = await supabase
        ?.from('tracking_payments')
        ?.upsert({
          booking_id: order?.id,
          readable_booking_id: order?.readableBookingId || order?.poNumber,
          amount: isST ? 0 : 19900,
          status: isST ? 'paid' : 'pending',
          expires_at: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000)?.toISOString(),
        }, { onConflict: 'booking_id' });

      if (tpErr) throw tpErr;

      if (isST) {
        const { data: result, error: invokeErr } = await supabase.functions.invoke('send-tracking-link', {
          body: { booking_id: order?.id },
        });
        if (invokeErr || !result?.success) {
          showNotification(`Tracking record created but WhatsApp failed: ${invokeErr?.message || result?.error}`, 'error');
          setDispatchingId(null);
          return;
        }
      }

      const { error: statusErr } = await supabase
        ?.from('buyer_bookings')
        ?.update({ status: 'in_transit', dispatched_at: new Date().toISOString() })
        ?.eq('id', order?.id);

      if (statusErr) throw statusErr;

      showNotification(
        isST
          ? `Dispatched! Tracking link sent to buyer.`
          : `Invoice Generated. Buyer will receive tracking link after ₹199 payment.`,
        'success'
      );

      loadActiveOrders();
    } catch (err) {
      console.error('Invoice Generated error:', err);
      showNotification(`Error: ${err?.message || 'Something went wrong'}`, 'error');
    }

    setDispatchingId(null);
  };

  return (
    <div className="space-y-6">

      {/* Notification */}
      {notification?.show && (
        <div
          className="fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-lg shadow-xl text-sm font-medium text-white"
          style={{ background: notification?.type === 'success' ? '#059669' : '#dc2626' }}
        >
          <Icon name={notification?.type === 'success' ? 'CheckCircle' : 'XCircle'} size={18} color="#ffffff" />
          {notification?.message}
        </div>
      )}

      {/* Admin VSR Modal */}
      {adminVsrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Substitute Vehicle</h3>
            <p className="text-xs text-gray-500 mb-5">
              Admin-initiated vehicle change for booking <strong>{adminVsrModal?.readableBookingId || adminVsrModal?.poNumber}</strong>.
              Will go through VAHAN check on approval.
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Current Vehicle</label>
                <div className="px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 font-mono text-sm text-gray-700">
                  {adminVsrModal?.assignedVehicle || '—'}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">
                  New Vehicle Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={adminVsrForm?.newVehicle}
                  onChange={e => setAdminVsrForm(f => ({ ...f, newVehicle: e.target.value.toUpperCase() }))}
                  placeholder="e.g. MH12AB5678"
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Reason</label>
                <select
                  value={adminVsrForm?.reason}
                  onChange={e => setAdminVsrForm(f => ({ ...f, reason: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  <option value="breakdown">Breakdown</option>
                  <option value="no_show">No Show</option>
                  <option value="compliance_fail">Compliance Fail</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setAdminVsrModal(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAdminVsrSubmit}
                disabled={adminVsrSubmitting}
                className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium disabled:opacity-60"
              >
                {adminVsrSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold" style={{ color: '#1f2937' }}>Active Bookings</h2>
          <p className="text-sm mt-0.5" style={{ color: '#4b5563' }}>
            {loading ? 'Loading…' : `${filteredOrders?.length} active booking${filteredOrders?.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
          style={{ backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}
        >
          <Icon name="CheckCircle" size={15} color="#16a34a" />
          Approved &amp; Active
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col gap-2">
        <input
          type="text"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="Search by Offer ID, factory, grade, buyer, destination..."
          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          <option value="all">All Statuses</option>
          <option value="approved">Approved</option>
          <option value="confirmed">Confirmed</option>
          <option value="do_generated">DO Generated</option>
          <option value="do_generated_partial">DO Partial</option>
          <option value="in_transit">In Transit</option>
        </select>
        <DateRangeFilter
          preset={preset} customFrom={customFrom} customTo={customTo}
          onPresetChange={setPreset} onCustomFromChange={setCustomFrom} onCustomToChange={setCustomTo}
        />
      </div>

      {/* Info Banner */}
      <div
        className="rounded-xl px-5 py-4 flex items-start gap-3"
        style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}
      >
        <Icon name="Info" size={18} color="#16a34a" />
        <p className="text-sm" style={{ color: '#166534' }}>
          These bookings have been approved and are now active. For <strong>Sugar + Truck</strong> orders, click the <strong>Offer ID</strong> to view or manage assigned trucks. Press <strong>Invoice Generated</strong> once the factory invoice is raised to dispatch tracking to the buyer.
        </p>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mr-3"></div>
          <p className="text-sm" style={{ color: '#6b7280' }}>Loading active bookings…</p>
        </div>
      )}

      {/* Orders Table */}
      {!loading && filteredOrders?.length === 0 ? (
        <div className="text-center py-16 rounded-xl" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <Icon name="PackageCheck" size={40} color="#86efac" />
          <p className="mt-3 text-sm font-medium" style={{ color: '#9ca3af' }}>No active bookings found</p>
          <p className="text-xs mt-1" style={{ color: '#6b7280' }}>Try adjusting your search or date filter</p>
        </div>
      ) : !loading && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #bbf7d0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>

          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#dcfce7' }}>
                  {['Offer ID', 'Factory Name', 'Grade', 'Qty (MT)', 'Rate/Ton', 'Total Amount', 'Buyer Email', 'Booking Type', 'Status', 'Date', 'Actions']?.map(col => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap"
                      style={{ color: '#166534', borderBottom: '1px solid #bbf7d0' }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredOrders?.map((order, idx) => (
                  <tr
                    key={order?.id}
                    style={{
                      background: idx % 2 === 0 ? '#ffffff' : '#f9fffe',
                      borderBottom: '1px solid #dcfce7'
                    }}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      {renderOfferIdCell(order)}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#374151', maxWidth: '180px' }}>
                      {order?.factoryName}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: '#dcfce7', color: '#166534' }}
                      >
                        {order?.sugarGrade}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-center" style={{ color: '#1e293b' }}>
                      {Number(order?.quantity)?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 font-medium" style={{ color: '#374151' }}>
                      ₹{Number(order?.ratePerTon)?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 font-semibold whitespace-nowrap" style={{ color: '#16a34a' }}>
                      {formatAmount(order?.totalAmount)}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#1e293b' }}>
                      {order?.buyer}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{
                          background: isBundleType(order?.bookingType) ? '#e0f2fe' : '#f3f4f6',
                          color: isBundleType(order?.bookingType) ? '#0369a1' : '#6b7280',
                        }}
                      >
                        {isBundleType(order?.bookingType) ? 'Sugar + Truck' : 'Sugar Only'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {pendingVsrBookingIds?.has(order?.id)
                        ? <span style={{ background: '#fef3c7', color: '#92400e' }} className="px-2 py-0.5 rounded-full text-xs font-semibold">🔄 Vehicle Request Pending</span>
                        : getStatusBadge(order?.status)
                      }
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#6b7280' }}>
                      {order?.bookingDate}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleInvoiceGenerated(order)}
                          disabled={dispatchingId === order?.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap"
                          style={{
                            background: dispatchingId === order?.id ? '#d1d5db' : '#d97706',
                            color: dispatchingId === order?.id ? '#9ca3af' : '#ffffff',
                            cursor: dispatchingId === order?.id ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {dispatchingId === order?.id ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Dispatching…
                            </>
                          ) : (
                            <>
                              <Icon name="FileCheck" size={12} />
                              Invoice Generated
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setAdminVsrModal(order);
                            setAdminVsrForm({ newVehicle: '', reason: 'breakdown' });
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap"
                          style={{ background: '#7c3aed', color: '#ffffff' }}
                        >
                          <Icon name="RefreshCw" size={12} />
                          Sub. Vehicle
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden divide-y" style={{ divideColor: '#bbf7d0' }}>
            {filteredOrders?.map((order) => (
              <div key={order?.id} className="p-4 space-y-3" style={{ background: '#ffffff' }}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="mb-1">
                      {renderOfferIdCell(order)}
                    </div>
                    <p className="text-sm font-medium mt-0.5" style={{ color: '#1e293b' }}>{order?.buyer}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>{order?.factoryName}</p>
                  </div>
                  {pendingVsrBookingIds?.has(order?.id)
                    ? <span style={{ background: '#fef3c7', color: '#92400e' }} className="px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap">🔄 Vehicle Request Pending</span>
                    : getStatusBadge(order?.status)
                  }
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg px-2.5 py-2" style={{ background: '#f0fdf4' }}>
                    <p className="text-xs" style={{ color: '#9ca3af' }}>Grade</p>
                    <p className="text-sm font-semibold" style={{ color: '#374151' }}>{order?.sugarGrade}</p>
                  </div>
                  <div className="rounded-lg px-2.5 py-2" style={{ background: '#f0fdf4' }}>
                    <p className="text-xs" style={{ color: '#9ca3af' }}>Qty (MT)</p>
                    <p className="text-sm font-semibold" style={{ color: '#1e293b' }}>{Number(order?.quantity)?.toLocaleString('en-IN')}</p>
                  </div>
                  <div className="rounded-lg px-2.5 py-2" style={{ background: '#f0fdf4' }}>
                    <p className="text-xs" style={{ color: '#9ca3af' }}>Rate/Ton</p>
                    <p className="text-sm font-semibold" style={{ color: '#374151' }}>₹{Number(order?.ratePerTon)?.toLocaleString('en-IN')}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs" style={{ color: '#9ca3af' }}>Total Amount</p>
                    <p className="text-base font-bold" style={{ color: '#16a34a' }}>{formatAmount(order?.totalAmount)}</p>
                  </div>
                  <p className="text-xs" style={{ color: '#6b7280' }}>{order?.bookingDate}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleInvoiceGenerated(order)}
                    disabled={dispatchingId === order?.id}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all"
                    style={{
                      background: dispatchingId === order?.id ? '#d1d5db' : '#d97706',
                      color: dispatchingId === order?.id ? '#9ca3af' : '#ffffff',
                      cursor: dispatchingId === order?.id ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {dispatchingId === order?.id ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Dispatching…
                      </>
                    ) : (
                      <>
                        <Icon name="FileCheck" size={14} />
                        Invoice Generated
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setAdminVsrModal(order);
                      setAdminVsrForm({ newVehicle: '', reason: 'breakdown' });
                    }}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold"
                    style={{ background: '#7c3aed', color: '#ffffff' }}
                  >
                    <Icon name="RefreshCw" size={14} />
                    Substitute Vehicle
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AssignedTrucksModal */}
      {assignedTrucksModal && (
        <AssignedTrucksModal
          booking={assignedTrucksModal}
          onClose={() => setAssignedTrucksModal(null)}
          onEditAssignment={() => setEditAssignmentModal(assignedTrucksModal)}
        />
      )}

      {/* TruckAssignmentModal (Edit) */}
      {editAssignmentModal && (
        <TruckAssignmentModal
          order={editAssignmentModal}
          onClose={() => setEditAssignmentModal(null)}
          onDone={() => {
            setEditAssignmentModal(null);
            loadActiveOrders();
          }}
        />
      )}
    </div>
  );
};

export default ActiveBookingsTab;

